import React, { Fragment, PureComponent } from 'react';
import {
    Grid,
    Button,
    Typography,
    Link,
    CircularProgress,
    Divider,
    Link as MuiLink,
} from '@mic3/platform-ui';
import ExpansionCard from 'app/components/molecules/ExpansionCard/ExpansionCard';
import DotMenu from 'app/components/molecules/DotMenu/DotMenu';
import { bind, memoize } from 'app/utils/decorators/decoratorUtils';
import {
    addEventDataAttribute,
    loadEventDataAttributes,
    makeEventAttributeIndexed,
    removeEventDataAttribute,
    removeEventIndexAttribute,
    updateEventDataAttribute
} from 'store/actions/stream/eventsActions';
import { connect } from 'react-redux';
import styled from 'styled-components';
import FormGenerator from '../Designer/Form/components/FormGenerator';
import Icon from 'app/components/atoms/Icon/Icon';
import { setActions } from 'store/actions/sidebar/sidebarActions';
import { loadBackgroundJobStatus } from 'store/actions/entities/entitiesActions';
import { showToastr } from 'store/actions/app/appActions';
import { buildAttributeMenu, getAttributeFieldType } from '../Stream/Events/EventTypes/eventTypesUtils';
import Loader from 'app/components/atoms/Loader/Loader';

const WAIT_INTERVAL = 5000;

const RemoveButton = styled(Button)`
    background-color: red !important;
    min-width: 160.12px !important;
    margin-bottom: 0px !important;
`;

const IndexButton = styled(Button)`
    min-width: 160.12px !important;
    margin-bottom: 1rem;
`;
const GridStyled = styled(Grid)`
    padding: 0 16px;
`;

const FormGeneratorStyled = styled(FormGenerator)`
    padding-left: 0;
    padding-right: 0;
`;

const DividerStyled = styled(Divider)`
    width: 100%;
    margin: 12px 0 !important;
`;

const Flex = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: end;
    align-items: center;
    width: 100%;
    gap: 1rem;
`;

const IconStyled = styled(Icon)`
    &::before {
        font-size: ${({ fontSize }) => fontSize} !important;
    }
`;

const ButtonStyled = styled(Button)`
    font-size: 12px !important;
    font-style: normal;
    font-weight: 500;
    line-height: 12px;
    padding: 3px 8px !important;
    height: 36.5px !important;
    letter-spacing: 0.42px;
    min-width: ${({ minWidth }) => minWidth} !important;
`;

const IndexSection = styled.div`
    margin: 16px 0;
    button {
        margin-bottom: 1rem;
    }
`;

const SelectAttributeWrapper = styled(Grid)`
    height: 100%;
`;
class EventTypeAttributeSidebar extends PureComponent {
    formRef = React.createRef();
    timerRef = React.createRef();

    constructor(props) {
        super(props);
        this.state = {
            isLoading: false,
            formLoading: false,
            isUnCollapsed: true,
            formData: props.attribute || {},
            filterBy: {
                field: 'eventType',
                op: '=',
                value: props.attribute.eventType
            },
            bgId: null,
            isJobFailed: false
        };
        this.defaultState = { ...this.state }
    }

    componentDidMount() {
        this.props.setActions(this.dotMenu(this.props.attribute?.state === 'created'));
    }

    componentDidUpdate(prevProps) {
        const { attribute, setActions } = this.props;
        if (attribute?.id !== prevProps?.attribute?.id) {
            setActions(this.dotMenu(attribute?.state === 'created'));
            // setting the job failed to false so in case if the indexing fails for an attribute then switching attribute will hide the error
            this.setState({ ...this.defaultState, formData: attribute, });
            
        }
    }

    componentWillUnmount() {
        this.clearTimer();
    }

    @bind
    @memoize()
    clearTimer(){
        if (this.timerRef.current) {
            clearTimeout(this.timerRef.current);
            this.timerRef.current = null;
        }
    }

    @bind
    buildComponents(expanded, indexed) {
        return [
            {
                properties: {
                    header: 'General Information',
                    expanded,
                    collapsible: true
                },
                type: 'panel',
                children: [
                    {
                        type: 'text',
                        properties: { label: 'Label', name: 'label' },
                        constraints: { required: true }
                    },
                    {
                        type: 'text',
                        properties: {
                            label: 'Value',
                            name: 'attribute',
                            disabled: !!indexed
                        },
                        constraints: { required: true }
                    }
                ]
            }
        ];
    }

    @bind
    handleCopyAttribute() {
        const {
            attribute,
            loadEventDataAttributes,
            addEventDataAttribute
        } = this.props;
        const { filterBy } = this.state;
        const {
            id,
            indexName,
            state,
            label,
            attribute: _attribute,
            ...rest
        } = attribute || {};
        const copiedData = {
            ...rest,
            label: label +  ' (copy)',
            attribute: _attribute + ' (copy)'
        };
        addEventDataAttribute(copiedData).then(result => {
            this.setState({ isLoading: false });
            if (!(result instanceof Error) && result.id) {
                loadEventDataAttributes({ filterBy });
            }
        });
    }

    @bind
    handleDeleteAttribute() {
        const {
            removeEventDataAttribute,
            attribute,
            loadEventDataAttributes
        } = this.props;
        const { filterBy } = this.state;
        removeEventDataAttribute(attribute?.id).then(async res => {
            if (!(res instanceof Error)) {
                loadEventDataAttributes({ filterBy });
            }
        });
    }

    @bind
    handleChange(formData) {
        this.setState(prevState => ({
            formData: { ...prevState.formData, ...(formData || {}) }
        }));
    }

    @bind
    @memoize()
    handleMenuClick(name){
        switch(name){
            case 'Copy':
                this.handleCopyAttribute();
                break;
            case 'Delete attribute':
                this.handleDeleteAttribute();
                break;
            default:
                return;

        }
    }

    @bind
    @memoize()
    dotMenu(isIndexed) {
        return (
            <DotMenu
                key={13}
                onItemClick={this.handleMenuClick}
                items={buildAttributeMenu(isIndexed)}
            />
        );
    }

    @bind
    togglePanelsView() {
        this.setState({ isUnCollapsed: !this.state.isUnCollapsed });
    }

    @bind
    onFormSubmit(event) {
        event.preventDefault();
        const {
            loadEventDataAttributes,
            onClose,
            updateEventDataAttribute
        } = this.props;

        const { filterBy } = this.state;
        this.formRef.current.isValidForm().then(({ data, errors }) => {
            if (!errors) {
                this.setState({ formLoading: true });
                updateEventDataAttribute(data).then(
                    async result => {
                        if (!(result instanceof Error) && result.id) {
                            if (loadEventDataAttributes) {
                                await loadEventDataAttributes({ filterBy });
                            }
                            onClose && onClose();
                        }
                        this.setState({ formLoading: false });
                    }
                );
            }
        });
    }

    @bind
    async reloadEventData(jobId, makeIndex){
        if (!jobId) return;

        this.setState({ bgId: jobId });
        this.clearTimer();

        this.timerRef.current = setTimeout(async () => {
            try {
                const bgResult = await loadBackgroundJobStatus(jobId);
                if (bgResult instanceof Error) {
                    this.setState({ isLoading: false });
                    return;
                }

                const { status } = bgResult?.primary || {};
                const { loadEventDataAttributes, setActions } = this.props;
                const { filterBy } = this.state;

                switch (status) {
                    case 'completed':
                        await loadEventDataAttributes({ filterBy });
                        this.setState({ isLoading: false });
                        setActions(this.dotMenu(makeIndex));
                        break;
                    case 'failed':
                        this.props.showToastr({
                            severity: 'error',
                            detail: 'There is some error in executing the job. Please try again'
                        });
                        this.setState({ isLoading: false, isJobFailed: true });
                        break;
                    default:
                        this.reloadEventData(jobId, makeIndex);
                }
            } catch (error) {
                this.setState({ isLoading: false });
                this.props.showToastr({
                    severity: 'error',
                    detail: 'An error occurred while checking job status'
                });
            }
        }, WAIT_INTERVAL);
    }

    @bind
    async handleIndexAttribute(makeIndex = true){
        const { attribute, makeEventAttributeIndexed, removeEventIndexAttribute } = this.props;

        try {
            this.setState({ isLoading: true, bgId: null, isJobFailed: false });

            const options = {
                name: attribute.label,
                eventDataAttribute: attribute.id,
            };

            const result = makeIndex
                ? await makeEventAttributeIndexed(options)
                : await removeEventIndexAttribute(options);

            if (result instanceof Error) {
                throw result;
            }

            await this.reloadEventData(result?.id, makeIndex);
        } catch (error) {
            this.setState({ isLoading: false });
            this.props.showToastr({
                severity: 'error',
                detail: 'An error occurred while processing the index operation'
            });
        }
    }

    @bind
    hasUnsavedChanges(formState, originalData) {
        if (!formState?.formTouched || formState?.data?.id !== originalData?.id) return null;

        return Object.keys(formState.data).some((key) => {
            return formState.data[key] !== originalData[key];
        });
    }

    @bind
    indexingInfoMessage(showIndexingInfo){
        if (!showIndexingInfo) return null;
        return (
            <Typography color="textSecondary">
                Indexing enables filters on attributes. Note that too many indexed fields decreases performance.&nbsp;
                <MuiLink href="https://support.affectli.com/en/classattributes" target="_blank">
                    Learn more
                </MuiLink>
                .
            </Typography>
        )
    }

    @bind
    renderIndexAttributeSection(isLoading, bgId, isJobFailed){
        return (
            <>
                {isLoading ? <CircularProgress size={24} /> : <IndexButton
                    onClick={() => this.handleIndexAttribute(true)}
                >
                    Index Attribute
                </IndexButton>}
                {isLoading && bgId && (
                    <Typography color="textSecondary">
                        Attribute is being indexed. Go to <Link to={`/abox/background-job/${bgId}`}>Background job</Link> to see the progress of this item.
                    </Typography>
                )}
                {!isLoading && bgId && isJobFailed && (
                    <Typography color="textSecondary">
                        The <Link to={`/abox/background-job/${bgId}`}>Background job</Link> is failed. Go to its details to see the error logs.
                    </Typography>
                )}
            </>
        )
    }

    @bind
    renderRemoveIndexAttributeSection(isLoading, bgId, isJobFailed){
        return (
            <>
                <RemoveButton onClick={() => this.handleIndexAttribute(false)}>
                    {isLoading ? <CircularProgress size={24} /> : 'Remove Indexing'}
                </RemoveButton>
                {isLoading && bgId && (
                    <Typography color="textSecondary">
                        Attribute indexing is being removed. Go to <Link to={`/abox/background-job/${bgId}`}>Background job</Link> to see the progress of this
                        item.
                    </Typography>
                )}
                {!isLoading && isJobFailed && (
                    <Typography color="textSecondary">
                        The <Link to={`/abox/background-job/${bgId}`}>Background job</Link> is failed. Go to its details to see the error logs.
                    </Typography>
                )}
            </>
        )
    }

    @bind
    renderIndexSection(){
        const { attribute } = this.props;
        const { isLoading, bgId, isJobFailed } = this.state;
        const alreadyIndexed = attribute?.state === 'created';
        const hasUnsavedChanges = this.hasUnsavedChanges(this.formRef?.current?.state, attribute)
        const showIndexingInfo = !isLoading && !alreadyIndexed && !isJobFailed;

        if(hasUnsavedChanges){
            return (
                <IndexSection>
                    <Typography color="textSecondary">
                        Please save changes to index an attribute.
                    </Typography>
                </IndexSection>
            )
        }

        return (
            <IndexSection>
                {alreadyIndexed ? (
                    this.renderRemoveIndexAttributeSection(isLoading, bgId, isJobFailed, attribute)
                ) : (
                    this.renderIndexAttributeSection(isLoading, bgId, isJobFailed)
                )}
                {this.indexingInfoMessage(showIndexingInfo)}
            </IndexSection>
        );
    }

    render() {
        const { attribute, attributeIcon } = this.props;
        const { isUnCollapsed, isLoading, formData, formLoading } = this.state;
        const { format, attribute: _attribute, label, type, id} = formData || {};
        const attributeData = { attribute: _attribute, format, label, id };
        const canIndex = formData?.format === 'none' && type !== 'boolean';

        return (
            <Fragment>
                {(isLoading || formLoading) && <Loader absolute backdrop />}
                {attribute ? (
                    <GridStyled container justify="space-around">
                        <DividerStyled />
                        <Flex>
                            <Button onClick={this.onFormSubmit}>
                                {formLoading ? (
                                    <CircularProgress size={24} color="primary" />
                                ) : (
                                    'Save'
                                )}
                            </Button>
                            <ButtonStyled
                                variant="outlined"
                                minWidth="38px"
                                size="small"
                                onClick={this.togglePanelsView}
                            >
                                <IconStyled
                                    size="sm"
                                    hexColor="#7391D0"
                                    fontSize="20px"
                                    name={
                                        isUnCollapsed
                                            ? 'unfold-less-horizontal'
                                            : 'unfold-more-horizontal'
                                    }
                                    title={`Click here to ${
                                        isUnCollapsed ? 'hide' : 'show'
                                    } the panels`}
                                />
                            </ButtonStyled>
                        </Flex>
                        <ExpansionCard
                            collapsible={false}
                            title={getAttributeFieldType(attribute)}
                            iconName={attributeIcon}
                        >
                            <Typography
                                variant="h3"
                                style={{
                                    fontWeight: 400,
                                    fontSize: '24px',
                                    lineHeight: '28px',
                                    letterSpacing: '-1.5%'
                                }}
                            >
                                {attribute?.label}
                            </Typography>
                        </ExpansionCard>
                        <FormGeneratorStyled
                            ref={this.formRef}
                            components={this.buildComponents(
                                isUnCollapsed,
                                attribute?.state === 'created'
                            )}
                            data={attributeData}
                            onChange={this.handleChange}
                        />
                        {canIndex && (
                            <ExpansionCard
                                collapsible={true}
                                title={'Indexing'}
                                expanded={isUnCollapsed}
                            >
                                {this.renderIndexSection()}
                            </ExpansionCard>
                        )}
                    </GridStyled>
                ): (
                    <SelectAttributeWrapper justify="center" alignItems="center" container>
                        <Typography variant="h5">Select attribute</Typography>
                    </SelectAttributeWrapper>
                )}
            </Fragment>
        );
    }
}

export default connect(
    (state, ownProps) => {
        const details = state.stream.events.attributesData?.data;
        const attribute = details?.eventDataAttributes?.find(
            att => att.id === ownProps.attributeId
        );
        return {
            isLoading: state.stream.events.typeData.isLoading,
            attribute
        };
    },
    {
        makeEventAttributeIndexed,
        addEventDataAttribute,
        removeEventIndexAttribute,
        updateEventDataAttribute,
        loadEventDataAttributes,
        setActions,
        removeEventDataAttribute,
        loadBackgroundJobStatus,
        showToastr
    }
)(EventTypeAttributeSidebar);
